/**
 * Deep Linking Utility
 *
 * Handles navigation from push notifications to specific screens
 * - Parse notification data (rfqId, applicationId, requestId)
 * - Route based on user role and notification type
 * - Support for web and mobile platforms
 */

/**
 * Handle deep link from notification
 * @param {Object} notificationData - Data from push notification
 * @param {string} notificationData.notification_type - Type of notification
 * @param {string} notificationData.rfq_id - Related RFQ ID (if applicable)
 * @param {string} notificationData.application_id - Related application ID (if applicable)
 * @param {string} notificationData.request_id - Related request ID (if applicable)
 * @param {string} notificationData.click_action - Action to perform
 * @param {Object} navigate - React Router navigate function
 * @param {string} userRole - Current user's role (recruiter, client, candidate, admin)
 */
export function handleDeepLink(notificationData, navigate, userRole) {
  console.log('[DeepLink] Handling deep link:', notificationData, 'User role:', userRole);

  const { notification_type, rfq_id, application_id, request_id, click_action } = notificationData;

  // Handle specific click actions
  if (click_action === 'NAVIGATE_TO_INBOX') {
    navigate(`/${userRole}/notifications`);
    return;
  }

  // Handle notification types
  switch (notification_type) {
    case 'rfq_inclusion_requested':
    case 'rfq_invited':
      handleRFQNotification(rfq_id, navigate, userRole);
      break;

    case 'new_application':
      handleApplicationNotification(application_id, rfq_id, navigate, userRole);
      break;

    case 'candidate_request':
      handleCandidateRequestNotification(request_id, navigate, userRole);
      break;

    case 'new_message':
      handleMessageNotification(navigate, userRole);
      break;

    case 'daily_digest':
      navigate(`/${userRole}/notifications`);
      break;

    default:
      // Default: go to notifications inbox
      navigate(`/${userRole}/notifications`);
      break;
  }
}

/**
 * Handle RFQ-related notification
 * @param {string} rfqId - RFQ ID
 * @param {Object} navigate - Navigate function
 * @param {string} userRole - User role
 */
function handleRFQNotification(rfqId, navigate, userRole) {
  if (userRole === 'recruiter' || userRole === 'admin') {
    // Navigate to RFQ list (or detail if we have the ID route)
    if (rfqId) {
      // If you have a detail route like /recruiter/rfqs/:id, use it
      // navigate(`/recruiter/rfqs/${rfqId}`);

      // For now, go to RFQ list
      navigate('/recruiter/rfqs');
    } else {
      navigate('/recruiter/rfqs');
    }
  } else if (userRole === 'client') {
    navigate('/client/rfqs');
  } else {
    navigate(`/${userRole}/dashboard`);
  }
}

/**
 * Handle application-related notification
 * @param {string} applicationId - Application ID
 * @param {string} rfqId - Related RFQ ID
 * @param {Object} navigate - Navigate function
 * @param {string} userRole - User role
 */
function handleApplicationNotification(applicationId, rfqId, navigate, userRole) {
  if (userRole === 'recruiter' || userRole === 'admin') {
    // Navigate to applications list
    navigate('/recruiter/applications');
  } else if (userRole === 'candidate') {
    // Navigate to candidate's applications
    navigate('/candidate/applications');
  } else {
    navigate(`/${userRole}/dashboard`);
  }
}

/**
 * Handle candidate request notification
 * @param {string} requestId - Request ID
 * @param {Object} navigate - Navigate function
 * @param {string} userRole - User role
 */
function handleCandidateRequestNotification(requestId, navigate, userRole) {
  if (userRole === 'recruiter' || userRole === 'admin') {
    // Navigate to candidate requests
    navigate('/recruiter/requests');
  } else if (userRole === 'client') {
    navigate('/client/candidates');
  } else {
    navigate(`/${userRole}/dashboard`);
  }
}

/**
 * Handle message notification
 * @param {Object} navigate - Navigate function
 * @param {string} userRole - User role
 */
function handleMessageNotification(navigate, userRole) {
  // Future: navigate to messages
  // For now, go to notifications
  navigate(`/${userRole}/notifications`);
}

/**
 * Parse deep link URL
 * Handles URLs like: appsytec://notification/rfq/123
 * @param {string} url - Deep link URL
 * @returns {Object|null} Parsed link data
 */
export function parseDeepLinkUrl(url) {
  if (!url) return null;

  try {
    // Example URL format: appsytec://notification/rfq/123
    // Or: https://appsytec.com/app/notification/rfq/123

    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    if (pathParts[0] === 'notification' || pathParts[1] === 'notification') {
      const startIndex = pathParts[0] === 'notification' ? 1 : 2;
      const type = pathParts[startIndex]; // 'rfq', 'application', 'request', etc.
      const id = pathParts[startIndex + 1];

      return {
        type,
        id,
        query: Object.fromEntries(urlObj.searchParams.entries())
      };
    }

    return null;

  } catch (error) {
    console.error('[DeepLink] URL parse error:', error);
    return null;
  }
}

/**
 * Build notification deep link
 * @param {string} notificationType - Type of notification
 * @param {Object} params - Parameters (rfq_id, application_id, etc.)
 * @returns {string} Deep link URL
 */
export function buildNotificationDeepLink(notificationType, params) {
  const baseUrl = 'appsytec://notification';

  switch (notificationType) {
    case 'rfq_inclusion_requested':
    case 'rfq_invited':
      return `${baseUrl}/rfq/${params.rfq_id}`;

    case 'new_application':
      return `${baseUrl}/application/${params.application_id}`;

    case 'candidate_request':
      return `${baseUrl}/request/${params.request_id}`;

    case 'new_message':
      return `${baseUrl}/message/${params.message_id}`;

    default:
      return `${baseUrl}/inbox`;
  }
}
